import { VegaButton, VegaInput, VegaPageNotification } from '@globalpayments/vega-react';
import { useState } from 'react';

import { ApiError } from '../../../api';
import DetailsModal from '../../../components/detailsModal';
import { invitationPartialSuccessStatus } from '../../../hooks/useInviteUser';
import { InviteUserRequest } from '../../../types/users';
import RoleCheckboxes from './RoleCheckboxes';

interface InviteUserDialogProps {
  error?: Error | null;
  isPending: boolean;
  onClose: () => void;
  onSubmit: (request: InviteUserRequest) => void;
  open: boolean;
}

const acceptedEmailDomains = ['e-hps.com', 'globalpayments.com', 'globalpay.com', 'tsys.com'] as const;
const acceptedEmailPattern = new RegExp(
  `^[^@\\s]+@(${acceptedEmailDomains.map((domain) => domain.replace('.', '\\.')).join('|')})$`,
  'i',
);

/** Collects and validates an OTP user invitation. */
const InviteUserDialog = ({ error, isPending, onClose, onSubmit, open }: InviteUserDialogProps) => {
  const [email, setEmail] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [roles, setRoles] = useState<string[]>([]);

  const normalizedEmail = email.trim();
  const isEmailValid = acceptedEmailPattern.test(normalizedEmail);
  // The API reports HTTP 422 when it created the user but could not deliver the login link, which
  // is a different outcome from a rejected invitation and must not read as "nothing happened".
  const isPartialSuccess = error instanceof ApiError && error.status === invitationPartialSuccessStatus;

  return (
    <DetailsModal
      backdrop="static"
      footerActions={
        <VegaButton
          disabled={!isEmailValid || isPending}
          label={isPending ? 'Inviting user' : 'Invite user'}
          onVegaClick={() =>
            onSubmit({
              email: normalizedEmail,
              displayName: displayName.trim() || undefined,
              roles,
            })
          }
        />
      }
      modalTitle="Invite user"
      onClose={onClose}
      open={open}>
      {error && (
        <VegaPageNotification
          duration={0}
          message={error.message}
          notificationTitle={isPartialSuccess ? 'User created, login link not sent' : 'Invitation failed'}
          type={isPartialSuccess ? 'warning' : 'error'}
        />
      )}
      <VegaInput
        autoValidation={false}
        hint={`Allowed domains: ${acceptedEmailDomains.join(', ')}`}
        isValid={email.length === 0 ? undefined : isEmailValid}
        label="Email"
        onVegaChange={(event) => setEmail(event.detail)}
        required
        type="email"
        value={email}
      />
      <VegaInput
        label="Display name"
        maxLength={500}
        onVegaChange={(event) => setDisplayName(event.detail)}
        value={displayName}
      />
      <RoleCheckboxes onChange={setRoles} roles={roles} />
    </DetailsModal>
  );
};

export default InviteUserDialog;
